import { useState } from "react";
import { Check, ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";
import type { CompanyData, FixedCost } from "./types";
import { formatCurrency, formatPercentage } from "./utils";

type Props = {
    data: CompanyData;
    onFixedCostsChange: (costs: FixedCost[]) => void;
    adCampaigns?: Array<{
        id: string;
        name: string;
        spend: number;
    }>;
};

export function MonthlyPnL({ data, onFixedCostsChange, adCampaigns = [] }: Props) {
    const [showFixedDetail, setShowFixedDetail] = useState(false);
    const [showAdDetail, setShowAdDetail] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [draftLabel, setDraftLabel] = useState("");
    const [draftAmount, setDraftAmount] = useState("");
    const collectedRevenue = data.collectedRevenue ?? data.totalRevenue;

    const pctOf = (value: number) => collectedRevenue > 0 ? (value / collectedRevenue) * 100 : 0;

    const startEdit = (cost: FixedCost) => {
        setEditingId(cost.id);
        setDraftLabel(cost.label);
        setDraftAmount(cost.amount.toString());
    };

    const commitEdit = () => {
        if (!editingId) return;
        const parsedAmount = parseInt(draftAmount.replace(/[^\d]/g, ""), 10);
        if (isNaN(parsedAmount) || !draftLabel.trim()) {
            setEditingId(null);
            return;
        }
        const next = data.fixedCosts.map((cost) =>
            cost.id === editingId ? { ...cost, label: draftLabel.trim(), amount: parsedAmount } : cost
        );
        onFixedCostsChange(next);
        setEditingId(null);
    };

    const removeCost = (id: string) => {
        onFixedCostsChange(data.fixedCosts.filter((cost) => cost.id !== id));
    };

    const addCost = () => {
        const newCost: FixedCost = {
            id: `fc-${Date.now()}`,
            label: "Nuevo costo fijo",
            amount: 0,
            currency: data.fixedCostsCurrency,
        };
        onFixedCostsChange([...data.fixedCosts, newCost]);
        startEdit(newCost);
    };

    return (
        <section className="overflow-hidden rounded-lg border border-gray-200 bg-white tabular-nums">
            <header className="px-5 py-4 border-b border-gray-100">
                <h2 className="text-base font-semibold text-gray-900">PyG consolidado de tu empresa</h2>
            </header>

            <div className="px-5 py-4">
                <PnLRow label="Recaudo total" hint="Órdenes entregadas o pagadas en el periodo" value={collectedRevenue} pct={100} bold />

                <PnLRow
                    label="Costo del producto"
                    hint="Costo unitario sincronizado desde Dropi"
                    value={data.totalProductCosts}
                    pct={pctOf(data.totalProductCosts)}
                />
                <PnLRow
                    label="Costos logísticos"
                    hint="Flete, envío, comisiones y devoluciones sincronizados desde Dropi"
                    value={data.totalLogisticsCosts}
                    pct={pctOf(data.totalLogisticsCosts)}
                />

                <ExpandableSummary
                    label="Inversión publicitaria"
                    hint="Pauta total de Meta en el periodo"
                    value={data.totalAdInvestment}
                    pct={pctOf(data.totalAdInvestment)}
                    expanded={showAdDetail}
                    onToggle={() => setShowAdDetail((value) => !value)}
                />
                {showAdDetail && (
                    <div className="mb-1 space-y-1 pl-6 border-l-2 border-gray-100">
                        {adCampaigns.length > 0 ? (
                            adCampaigns
                                .filter((campaign) => campaign.spend > 0)
                                .sort((a, b) => b.spend - a.spend)
                                .slice(0, 10)
                                .map((campaign) => (
                                    <DetailLine key={campaign.id} label={campaign.name} value={campaign.spend} pct={pctOf(campaign.spend)} />
                                ))
                        ) : (
                            <DetailLine label="Pauta total Meta" value={data.totalAdInvestment} pct={pctOf(data.totalAdInvestment)} />
                        )}
                    </div>
                )}

                <Divider />
                <PnLRow
                    label="Utilidad operativa"
                    hint="Recaudo - costos variables - inversión publicitaria"
                    value={data.operatingProfit}
                    pct={pctOf(data.operatingProfit)}
                    highlight="emerald"
                    bold
                />
                <Divider />

                <div className="py-3">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => setShowFixedDetail((value) => !value)}
                            className="flex items-center gap-1.5 text-left flex-1"
                            type="button"
                        >
                            <span>
                                <span className="block text-sm text-gray-700">Costos fijos mensuales</span>
                                <span className="block text-[11px] text-gray-400">Configurados desde onboarding y editables aqui</span>
                            </span>
                            {showFixedDetail
                                ? <ChevronDown className="w-4 h-4 text-gray-400" />
                                : <ChevronRight className="w-4 h-4 text-gray-400" />}
                        </button>
                        <div className="flex items-baseline gap-3">
                            <span className="text-sm font-medium text-gray-700">{formatCurrency(data.totalFixedCosts, data.fixedCostsCurrency)}</span>
                            <span className="text-xs text-gray-400 w-12 text-right">{formatPercentage(pctOf(data.totalFixedCosts))}</span>
                        </div>
                    </div>

                    {showFixedDetail && (
                        <div className="mt-3 space-y-1 pl-6 border-l-2 border-gray-100">
                            {data.fixedCosts.map((cost) => (
                                <div key={cost.id} className="flex items-center gap-2 py-1.5 group">
                                    {editingId === cost.id ? (
                                        <>
                                            <input
                                                autoFocus
                                                type="text"
                                                value={draftLabel}
                                                onChange={(event) => setDraftLabel(event.target.value)}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") commitEdit();
                                                    if (event.key === "Escape") setEditingId(null);
                                                }}
                                                className="flex-1 text-xs px-2 py-1 border border-gray-300 rounded outline-none focus:border-slate-500"
                                                placeholder="Concepto"
                                            />
                                            <input
                                                type="text"
                                                value={draftAmount}
                                                onChange={(event) => setDraftAmount(event.target.value)}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") commitEdit();
                                                    if (event.key === "Escape") setEditingId(null);
                                                }}
                                                className="w-32 text-xs text-right px-2 py-1 border border-gray-300 rounded outline-none focus:border-slate-500"
                                                placeholder="Valor mensual"
                                            />
                                            <button onClick={commitEdit} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" aria-label="Guardar" type="button">
                                                <Check className="w-3.5 h-3.5" />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <span className="flex-1 text-xs text-gray-600">{cost.label}</span>
                                            <span className="text-xs text-gray-700 tabular-nums">{formatCurrency(cost.amount, cost.currency ?? data.fixedCostsCurrency)}</span>
                                            <button
                                                onClick={() => startEdit(cost)}
                                                className="p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                                aria-label="Editar"
                                                type="button"
                                            >
                                                <Pencil className="w-3 h-3" />
                                            </button>
                                            <button
                                                onClick={() => removeCost(cost.id)}
                                                className="p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                                aria-label="Eliminar"
                                                type="button"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            ))}

                            <button onClick={addCost} className="mt-2 inline-flex items-center gap-1.5 text-xs text-slate-700 hover:text-slate-900 font-medium" type="button">
                                <Plus className="w-3 h-3" />
                                Agregar costo fijo
                            </button>
                        </div>
                    )}
                </div>

                <ThickDivider />
                <PnLRow
                    label="Utilidad neta"
                    hint="Utilidad operativa - costos fijos"
                    value={data.netProfit}
                    pct={pctOf(data.netProfit)}
                    highlight="slate"
                    bold
                    size="big"
                />
            </div>
        </section>
    );
}

function ExpandableSummary({ label, hint, value, pct, expanded, onToggle }: {
    label: string;
    hint?: string;
    value: number;
    pct: number;
    expanded: boolean;
    onToggle: () => void;
}) {
    return (
        <div className="py-3">
            <div className="flex items-center justify-between">
                <button onClick={onToggle} className="flex items-center gap-1.5 text-left flex-1" type="button">
                    <span>
                        <span className="block text-sm text-gray-700">{label}</span>
                        {hint ? <span className="block text-[11px] text-gray-400">{hint}</span> : null}
                    </span>
                    {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                </button>
                <div className="flex items-baseline gap-3">
                    <span className="text-sm font-medium text-gray-700">{formatCurrency(value)}</span>
                    <span className="text-xs text-gray-500 w-12 text-right">{formatPercentage(pct)}</span>
                </div>
            </div>
        </div>
    );
}

function DetailLine({ label, value, pct }: { label: string; value: number; pct: number }) {
    return (
        <div className="flex min-w-0 items-center justify-between gap-3 py-1.5">
            <span className="min-w-0 truncate text-xs text-gray-600">{label}</span>
            <div className="flex shrink-0 items-baseline gap-3">
                <span className="text-xs text-gray-700 tabular-nums">{formatCurrency(value)}</span>
                <span className="text-[11px] text-gray-400 w-12 text-right">{formatPercentage(pct)}</span>
            </div>
        </div>
    );
}

function PnLRow({ label, hint, value, pct, cost, highlight, bold, size }: {
    label: string;
    hint?: string;
    value: number;
    pct: number;
    cost?: boolean;
    highlight?: "emerald" | "slate";
    bold?: boolean;
    size?: "big" | "normal";
}) {
    const labelClasses = bold ? "font-semibold" : "";
    const valueClasses = (() => {
        if (size === "big") return "text-lg font-bold";
        if (bold) return "font-semibold";
        return "";
    })();
    const colorClasses = (() => {
        if (value < 0) return "text-red-900";
        if (highlight === "emerald") return "text-emerald-900";
        if (highlight === "slate") return "text-slate-900";
        if (cost) return "text-gray-700";
        return "text-gray-900";
    })();
    const highlightClasses = (() => {
        if (!highlight) return "";
        if (value < 0) return "px-3 -mx-3 rounded-lg bg-red-50";
        return `px-3 -mx-3 rounded-lg ${highlight === "emerald" ? "bg-emerald-50" : "bg-slate-50"}`;
    })();

    return (
        <div className={`flex items-baseline justify-between py-2.5 ${highlightClasses}`}>
            <span className={`${colorClasses}`}>
                <span className={`block text-sm ${labelClasses}`}>{label}</span>
                {hint ? <span className="block text-[11px] font-normal text-gray-400">{hint}</span> : null}
            </span>
            <div className="flex items-baseline gap-3">
                <span className={`tabular-nums ${valueClasses} ${colorClasses}`}>{formatCurrency(value)}</span>
                <span className="text-xs text-gray-500 w-12 text-right">{formatPercentage(pct)}</span>
            </div>
        </div>
    );
}

function Divider() {
    return <div className="border-t border-gray-100 my-1" />;
}

function ThickDivider() {
    return <div className="border-t-2 border-gray-200 my-2" />;
}
